import { useMemo, useState } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  PinnableCell,
  DescriptionTitleEditor,
  useEstimateEditorSettings,
  useEstimateEditorStore,
  usePresetFieldNames,
  useVisibilityModeCellClassName,
  VisibilityButton,
  useEstimateTableSettings,
  useEstimateStructure,
} from "@tools";
import { Alert } from "@components";
import { Grid, IconButton, Tooltip } from "@material-ui/core";
import { LastPinnedCellActions } from "../components/LastPinnedCellActions";
import { Add } from "@material-ui/icons";
import classnames from "classnames";
import useCellStyle from "@tools/EstimateEditor/styles/CommonTableCellStyles.style";

export const TaskDescriptionCell = observer(({
  columnSettings,
  section,
  isLastPinnedCell,
  isLastPinnableCell,
}) => {
  const { t } = useTranslation();
  const { allowEdition, isProposal, isLibrary } = useEstimateEditorSettings();
  const { showActionsOnScroll } = useEstimateTableSettings();
  const { visibilityMode } = useEstimateEditorStore();
  const structure = useEstimateStructure();
  const className = useVisibilityModeCellClassName(columnSettings.hide);
  const { getFieldName } = usePresetFieldNames(isProposal);
  const cellClasses = useCellStyle();
  
  const [ addAlertVisible, showAddAlert ] = useState(false);
  
  const { id, descriptionIndex, hide, pinLeftPx, displayName } = columnSettings;
  const field = `pt-desc${descriptionIndex ? descriptionIndex + 1 : ""}`;
  
  const { usedAllDescriptionColumns } = section;
  
  const showActions = useMemo(() => (
    showActionsOnScroll ? isLastPinnedCell : isLastPinnableCell
  ), [ isLastPinnedCell, isLastPinnableCell, showActionsOnScroll ]);
  
  const handleAddAlertOpen = () => {
    showAddAlert(true);
  }
  
  const handleAddAlertClose = (addToAll) => () => {
    structure.addDescriptionColumn(addToAll ? undefined : section?.id);
    showAddAlert(false);
  }
  
  return <>
    <PinnableCell
      resizeable
      isHeader
      widerPaddings
      colName={ id }
      
      allowOverflowDisplay={isLastPinnedCell}
      pinnedLeft={typeof pinLeftPx === "number"}
      
      actions={ showActions && <LastPinnedCellActions section={section} /> }
      allowEdition={ allowEdition && !isLibrary }
      columnSettings={ columnSettings }
      innerClassName="items-end"
    >
      <Grid
        item container
        alignItems="center"
        wrap="nowrap"
        className={classnames("text-left", cellClasses.sizeXSmall, className)}
      >
        {
          allowEdition
            ? <DescriptionTitleEditor columnSettings={columnSettings}/>
            : <span className={`preset-tableBreakdownHeader ${field} ml-0-5 break-word`}>
              { getFieldName(field, undefined, undefined, displayName) }
            </span>
        }
        {
          allowEdition && showActions && !usedAllDescriptionColumns &&
          <>
            <Tooltip
              title={t("views.editor.breakdown_table.add_description_column")}
            >
              <IconButton
                onClick={handleAddAlertOpen}
                size="tiny"
                color="primary"
                className="text-sm p-0-5 ml-1"
              >
                <Add />
              </IconButton>
            </Tooltip>
            <Alert
              title={t("views.editor.breakdown_table.alert_titles.add")}
              isOpen={addAlertVisible}
              intermediateText={t("views.editor.breakdown_table.alert_titles.this_section")}
              acceptText={t("views.editor.breakdown_table.alert_titles.all_sections")}
              onCancel={() => showAddAlert(false)}
              onIntermediate={handleAddAlertClose(false)}
              onAccept={handleAddAlertClose(true)}
            />
          </>
        }
        
        {
          visibilityMode &&
          <VisibilityButton
            size="tiny"
            hidden={hide}
            onChange={columnSettings.setHide}
          />
        }
      </Grid>
    </PinnableCell>
  </>
})

TaskDescriptionCell.propTypes = {
  section: object.isRequired,
  columnSettings: object.isRequired,
  isLastPinnedCell: bool,
  isLastPinnableCell: bool,
}